import { isString } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

import { useBoolean } from 'src/hooks/use-boolean';

import { fCurrency } from 'src/utils/format-number';

import Iconify from 'src/components/iconify';
import { useTranslate } from 'src/locales';
import i18n from 'src/locales/i18n';
import {
  useGetOfferByIdQuery,
  useLogOfferActionMutation,
} from 'src/services/applications/applications.service';
import { OfferAction, OfferStatus } from 'src/services/applications/applications.types';
import { DocumentType } from 'src/services/files/file.types';
import { ComplianceRequirementType } from 'src/services/shared/shared.types';
import { hideManagerOfferActionModal } from 'src/store/slices/applications/applicationsSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';

import { ConfirmDialog } from 'src/components/custom-dialog';

import { useUploadFileMutation } from 'src/services/files/file.service';
import { BusinessErrorCodes } from 'src/types/business-errors';

type Props = {
  open: boolean;
};

const generateActionOfferError = (e: any, action: OfferAction): string => {
  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.InvalidOfferStatus) {
      return i18n.t('applications.api.action_offer.invalid_state');
    }

    if (e?.data.error_code === BusinessErrorCodes.PlacementExists) {
      return i18n.t('applications.api.action_offer.placement_exists');
    }
  }

  return i18n.t('applications.api.log_offer_response.default_error');
};

export default function ManagerOfferActionModal({ open }: Props) {
  const { t } = useTranslate();

  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useAppDispatch();

  const selectedOfferId = useAppSelector((state) => state.applications.selectedOfferId);

  const confirmAccept = useBoolean();

  const confirmDecline = useBoolean();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [selectedDocuments, setSelectedDocuments] = useState<
    { file_id: string; type: string }[]
  >([]);

  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);

  const [uploadFile] = useUploadFileMutation();

  const [currentView, setCurrentView] = useState<'detail' | 'documents'>('detail');

  const { currentData: offer, isLoading } = useGetOfferByIdQuery(
    { offerId: selectedOfferId as string },
    { skip: !selectedOfferId }
  );

  const onClose = () => {
    dispatch(hideManagerOfferActionModal());
  };

  const [logOfferAction, { isLoading: isSubmitting }] = useLogOfferActionMutation();

  const requiredDocuments = useMemo(() => {
    if (!offer || !Array.isArray(offer.compliance_requirements)) return [];

    return offer.compliance_requirements.filter((req) => req.requirement_type === ComplianceRequirementType.DOCUMENT).map((req) => ({
      type: req.item_type as DocumentType,
      custom_type_name: req.custom_type_name as string
    }));
  }, [offer]);

  const handleDecline = () => {
    confirmDecline.onTrue();
  }

  const handleAccept = () => {
    if (requiredDocuments.length > 0 && currentView === 'detail') {
      setCurrentView('documents');
    }
    else {
      confirmAccept.onTrue();
    }
  }

  const onActionOffer = async (action: OfferAction) => {
    // check if all required documents are uploaded
    // check if the correct types of documents are uploaded
    if (action === OfferAction.Accept && requiredDocuments.length > 0) {
      const missingDocuments = requiredDocuments.filter((doc) => !selectedDocuments.some((selectedDoc) => selectedDoc.type === doc.type));
      if (requiredDocuments.length > 0 && (selectedDocuments.length !== requiredDocuments.length || missingDocuments.length > 0)) {
        setErrorMsg(t('applications.candidate_offer_modal.documents.errors.required'));
        return;
      }
    }

    try {
      await logOfferAction({
        offerId: selectedOfferId as string,
        action,
        documents: selectedDocuments,
      }).unwrap();

      if (action === OfferAction.Accept) {
        enqueueSnackbar(
          t('applications.api.log_offer_response.success', {
            role: offer?.position_title,
            company: offer?.employer_name,
          }),
          {
            variant: 'success',
          }
        );

        confirmAccept.onFalse();
        onClose();
      } else {
        enqueueSnackbar(
          t('applications.api.log_offer_response.default_error', {
            role: offer?.position_title,
            company: offer?.employer_name,
          }),
          {
            variant: 'success',
          }
        );

        onClose();
      }
    } catch (e: any) {
      enqueueSnackbar(generateActionOfferError(e, action), {
        variant: 'error',
      });
    }
  };

  const handleFileUpload = useCallback(async (documentType: DocumentType) => {
    if (!currentFile) return;

    try {
      setUploadingDocument(true);

      const uploadedFile = await uploadFile({
        file: currentFile,
        type: 'document',
      }).unwrap();

      setSelectedDocuments((prev) => {
        const existingIndex = prev.findIndex((doc) => doc.type === documentType);
        if (existingIndex !== -1) {
          return prev.map((doc) => {
            if (doc.type === documentType) {
              return { file_id: uploadedFile.id, type: documentType };
            }
            return doc;
          });
        }

        return [...prev, { file_id: uploadedFile.id, type: documentType }];
      });

      // Reset form and move to next document
      setCurrentFile(null);
      setCurrentDocumentIndex(prev => prev + 1);

      enqueueSnackbar(t('applications.manager_offer_action_modal.document_upload_success'), {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(t('applications.manager_offer_action_modal.document_upload_error'), {
        variant: 'error',
      });
    } finally {
      setUploadingDocument(false);
    }
  }, [currentFile, enqueueSnackbar, t, uploadFile]);

  const handleRemoveDocument = (type: DocumentType) => {
    setSelectedDocuments((prev) => prev.filter((doc) => doc.type !== type));

    // Find the index of the removed document in the requiredDocuments array
    const removedIndex = requiredDocuments.findIndex(doc => doc.type === type);

    // If we've already moved past this document, go back to it
    if (removedIndex < currentDocumentIndex) {
      setCurrentDocumentIndex(removedIndex);
    }
  };

  const offerValid = useMemo(() => offer?.status === OfferStatus.SENT, [offer?.status]);

  const renderOfferDetails = (
    <Grid container spacing={2}>
      <Grid xs={6}>
        <TextField
          fullWidth
          name="email"
          label={t('external.verification.form.company')}
          value={offer?.employer_name}
          
          disabled
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
          }}
        />
      </Grid>

      <Grid xs={6}>
        <TextField
          fullWidth
          value={offer?.position_title}
          name="email"
          label={t('external.verification.form.position')}
          
          disabled
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
          }}
        />
      </Grid>

      <Grid xs={6}>
        <MobileDatePicker
          value={new Date(`${offer?.start_date}`)}
          name="start_date"
          label={t('external.verification.form.start')}
          disabled
          sx={{
            width: '100%',
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
          }}
        />
      </Grid>

      <Grid xs={6}>
        {!offer?.end_date ? (
          <TextField
            fullWidth
            value={t('enums.employment_type.permanent')}
            name="email"
            label={t('external.verification.form.end')}
            
            disabled
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: theme.palette.text.primary,
              },
            }}
          />
        ) : (
          <MobileDatePicker
            value={new Date(`${offer?.end_date}`)}
            name="start_date"
            label={t('external.verification.form.end')}
            disabled
            sx={{
              width: '100%',
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: theme.palette.text.primary,
              },
            }}
          />
        )}
      </Grid>

      <Grid xs={12}>
        <TextField
          fullWidth
          value={
            offer?.pay_rate
              ? `${fCurrency(offer?.pay_rate)} ${t(`enums.rate_period_per.${offer?.rate_period}`)}`
              : t('common.unknown')
          }
          name="email"
          label={t('external.verification.form.salary')}
          
          disabled
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
          }}
        />
      </Grid>

      <Grid xs={12}>
        <TextField
          fullWidth
          value={offer?.description || t('common.none_provided')}
          name="description"
          label={t('external.verification.form.description')}
          
          multiline
          rows={4}
          disabled
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
          }}
        />
      </Grid>

      <Grid xs={12} flexDirection="row" spacing={2}>
        <LoadingButton
          loading={isSubmitting}
          fullWidth
          variant="outlined"
          onClick={handleDecline}
          disabled={!offerValid}
        >
          {t('common.log_decline', { defaultValue: 'Log Rejection' })}
        </LoadingButton>

        <LoadingButton
          onClick={handleAccept}
          loading={isSubmitting}
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          disabled={!offerValid}
        >
          {t('common.log_accept', { defaultValue: 'Log Acceptance' })}
        </LoadingButton>
      </Grid>
    </Grid>
  );

  const renderDocumentRequests = (
    <>
      {errorMsg && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {errorMsg}
        </Typography>
      )}

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {t('applications.manager_offer_action_modal.documents.title', { defaultValue: 'Required Documents' })}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('applications.manager_offer_action_modal.documents.description', { defaultValue: 'Please upload the required documents to complete this offer.' })}
      </Typography>

      {/* Document navigation */}
      {requiredDocuments.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('applications.manager_offer_action_modal.documents.document_status', { defaultValue: 'Document Status' })}
          </Typography>

          <List>
            {requiredDocuments.map((doc, index) => {
              const isUploaded = selectedDocuments.some(selected => selected.type === doc.type);
              const isCurrent = index === currentDocumentIndex;

              return (
                <ListItem
                  key={doc.type}
                  sx={{
                    bgcolor: isCurrent ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                  }}
                  secondaryAction={
                    isUploaded ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveDocument(doc.type as DocumentType)}
                      >
                        {t('common.remove')}
                      </Button>
                    ) : (
                      index !== currentDocumentIndex && (
                        <Button
                          size="small"
                          onClick={() => setCurrentDocumentIndex(index)}
                        >
                          {t('common.upload')}
                        </Button>
                      )
                    )
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isUploaded && <Iconify icon="eva:checkmark-circle-fill" color="success.main" sx={{ mr: 1 }} />}
                        {t(`enums.document_type.${doc.type}`)}
                      </Box>
                    }
                    secondary={doc.type === DocumentType.OTHER && doc.custom_type_name}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: isUploaded ? 'success.main' : 'text.primary',
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}


      {/* current document upload */}
      {requiredDocuments.length > 0 && currentDocumentIndex < requiredDocuments.length && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            {t(`enums.document_type.${requiredDocuments[currentDocumentIndex].type}`)}
            {requiredDocuments[currentDocumentIndex].type === DocumentType.OTHER &&
              requiredDocuments[currentDocumentIndex].custom_type_name &&
              ` - ${requiredDocuments[currentDocumentIndex].custom_type_name}`}
          </Typography>

          <Box sx={{ border: '1px dashed rgba(145, 158, 171, 0.2)', borderRadius: 1, p: 2 }}>
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setCurrentFile(e.target.files[0]);
                }
              }}
            />
            <label htmlFor="file-upload">
              <Button
                component="span"
                variant="outlined"
                fullWidth
                startIcon={<Iconify icon="eva:cloud-upload-fill" />}
              >
                {t('applications.manager_offer_action_modal.documents.select_file', { defaultValue: 'Select File' })}
              </Button>
            </label>

            {currentFile && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">{currentFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(currentFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            )}
          </Box>

          <LoadingButton
            loading={uploadingDocument}
            disabled={!currentFile}
            variant="contained"
            onClick={() => handleFileUpload(requiredDocuments[currentDocumentIndex].type)}
          >
            {t('applications.manager_offer_action_modal.documents.upload_button', { defaultValue: 'Upload Document' })}
          </LoadingButton>
        </Stack>
      )}

    </>
  );

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
        <DialogTitle>
          {t('applications.manager_offer_action_modal.title')}
        </DialogTitle>

        <DialogContent>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 300,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2, mb: 1 }}>
              {currentView === 'detail' && renderOfferDetails}
              {currentView === 'documents' && renderDocumentRequests}
            </Box>
          )}
        </DialogContent>

        <DialogActions>

          {
            currentView === 'detail' && (
              <Button onClick={onClose}>{t('common.close')}</Button>
            )
          }

          {
            currentView === 'documents' && (
              <>
                <Button variant="outlined" onClick={() => setCurrentView('detail')}>
                  {t('common.back')}
                </Button>

                <LoadingButton
                  loading={isSubmitting}
                  variant="contained"
                  onClick={() => onActionOffer(OfferAction.Accept)}
                  disabled={selectedDocuments.length < requiredDocuments.length}
                >
                  {t('common.log_accept', { defaultValue: 'Log Acceptance' })}
                </LoadingButton>
              </>
            )
          }
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmAccept.value}
        title={t('applications.manager_offer_action_modal.confirm_accept.title')}
        content={t('applications.manager_offer_action_modal.confirm_accept.description')}
        action={
          <LoadingButton
            variant="contained"
            color="success"
            onClick={() => onActionOffer(OfferAction.Accept)}
            loading={isSubmitting}
          >
            {t('common.confirm')}
          </LoadingButton>
        }
        onClose={confirmAccept.onFalse}
      />

      <ConfirmDialog
        open={confirmDecline.value}
        title={t('applications.manager_offer_action_modal.confirm_decline.title')}
        content={t('applications.manager_offer_action_modal.confirm_decline.description')}
        action={
          <LoadingButton
            variant="contained"
            color="error"
            onClick={() => onActionOffer(OfferAction.Decline)}
            loading={isSubmitting}
          >
            {t('common.confirm')}
          </LoadingButton>
        }
        onClose={confirmDecline.onFalse}
      />

    </>
  );
}